import React, { createContext, useContext } from "react";
import { Loader } from "rsuite";
import { useDraftStatus } from "../hooks/useDraft";
import { useDraftId } from "../hooks/useDraftId";
const DraftGraphContext = createContext({});
/**
 * Resolves the active draft and provides its graph URI to descendants. While a
 * draft is in play (`?draftId=` present) but still loading, it holds the
 * subtree behind a loader so descendant queries can't fire before the URI is
 * known — guaranteeing the *first* draft read is already scoped. On load
 * failure it falls through with `undefined` (same as public browsing) rather
 * than blocking forever.
 *
 * Place inside the Router (for `useDraftId`) and AuthContext (for the fetch).
 */
export function DraftGraphProvider({ children, }) {
    const draftId = useDraftId();
    const { draft, loading } = useDraftStatus(draftId);
    // Resolved only when we hold THIS draft's data — not mid-fetch, and not a
    // stale draft left over from a previous route. Computed during render (not in
    // an effect) so we never expose a missing/wrong URI for even one frame.
    const resolved = !draftId || (!loading && draft?.id === draftId);
    if (!resolved) {
        return React.createElement(Loader, { center: true, content: "Loading draft\u2026" });
    }
    // Only surface a URI while a draft is actually in play, so a stale draft
    // object can't leak its graph into non-draft pages.
    const contextValue = draftId
        ? {
            draftGraphUri: draft?.draft?.draft_graph_uri,
            imCode: draft?.account?.code ?? undefined,
        }
        : {};
    return (React.createElement(DraftGraphContext.Provider, { value: contextValue }, children));
}
/** The active draft's named-graph URI, or `undefined` when not editing a draft. */
export function useDraftGraphUri() {
    return useContext(DraftGraphContext).draftGraphUri;
}
export function useDraftImCode() {
    return useContext(DraftGraphContext).imCode;
}
